import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { BadgeSelect } from '@/app/(main)/clients/create/components/BadgeSelect';
import { StayAccommodationTypesField } from '@/app/(main)/clients/create/components/StayAccommodationTypesField';
import { ComfortDetails } from '@/app/(main)/clients/create/components/CreateClientComformDetails';
import { PersonalInfoSection } from '@/app/(main)/clients/create/components/PersonalInfoSection';
import { Section } from '@/app/(main)/clients/create/components/Section';
import Tabs from '@/app/components/Tabs';
import { TextArea } from '@/app/components/form/TextArea';
import { DRINKING_PREFERENCE_OPTIONS, INTEREST_PREFERENCE_OPTIONS, PILLOW_PREFERENCE_OPTIONS, ROOM_PREFERENCE_OPTIONS, SPA_PREFERENCE_OPTIONS, type CreateClientFormData, type DrinkingPreference, type EditClientResponse, type InterestPreference, type PillowPreference, type RoomPreference } from '@/types/clientsApiTypes';
import { RELATED_DATA_KEYS, type RelatedDataKeys } from '../../hooks/useUpdateTravelPreferencesWithRelatedData/types';

interface TravelPreferencesByCategoryProps {
  onUpdateTravelPreferencesWithRelatedData?: (fieldKey: RelatedDataKeys, fieldName: string, fieldValue: unknown, fullPath: string) => Promise<EditClientResponse | undefined>;
}

const TravelFlightPreferences = (props: TravelPreferencesByCategoryProps) => {
  const basePath = `primary_passenger.preference_data`;
  const {
    register,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  return (
    <TextArea
      {...register(`${basePath}.travel`, {
        onBlur: (e) => {
          props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.travel, 'travel', e.target.value, `${basePath}.travel`);
        },
      })}
      disableResize
      placeholder="Please let us know any flight preferences that may be important for booking, including airlines, seating, cabin class, or special requests."
      rows={4}
      errors={errors}
    />
  );
};

const StayAccommodationPreferences = (props: TravelPreferencesByCategoryProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();
  const basePath = `primary_passenger.preference_data`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <h5 className="font-sans text-lg font-semibold leading-normal">Accommodation Preferences</h5>
        <p className="font-sans text-body-small text-grey-700">
          While we know every trip is unique, please indicate your favorite types of accommodations by selecting up to
          three categories.
        </p>
      </div>
      <Controller
        name={`${basePath}.stay.accommodation_types`}
        control={control}
        render={({ field }) => (
          <StayAccommodationTypesField
            value={field.value}
            onChange={(next) => {
              field.onChange(next);
              props.onUpdateTravelPreferencesWithRelatedData?.(
                RELATED_DATA_KEYS.stay,
                'accommodation_types',
                next,
                `${basePath}.stay.accommodation_types`
              );
            }}
          />
        )}
      />

      <div className="mt-4">
        <TextArea
          {...register(`${basePath}.stay.preferences_note`, {
            onBlur: (e) => {
              props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.stay, 'preferences_note', e.target.value, `${basePath}.stay.preferences_note`);
            },
          })}
          disableResize
          placeholder="What else should we know? Please share anything relevant to your ideal trip. If you could tell us about some of your favorite stays of all time, we're better able to match you with similar types of stays in the future."
          rows={4}
          errors={errors}
        />
      </div>
    </div>
  );
};

const TravelPreferencesLifestyle = (props: TravelPreferencesByCategoryProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();
  const basePath = `primary_passenger.preference_data`;

  return (
    <div className="rounded-[14px] bg-[#F2F6FC] flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-0.5">
          <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">General Interests</h5>
          <p className="font-sans text-base font-normal leading-6 text-[#495057]">
            What are your key priorities when traveling? Select up to three.
          </p>
        </div>
        <Controller
          name={`${basePath}.general_interests`}
          control={control}
          render={({ field }) => (
            <BadgeSelect<InterestPreference>
              label=""
              options={INTEREST_PREFERENCE_OPTIONS}
              value={field.value ?? []}
              onChange={(value) => {
                field.onChange(value);
                props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'general_interests', value, `${basePath}.general_interests`);
              }}
              visibleCount={10}
              collapsible
            />
          )}
        />
      </div>

      <div className="h-px bg-[#D9E2F3]" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">Beverage Preferences</h5>
          <p className="font-sans text-base font-normal leading-6 text-[#495057]">
            While we know your beverage preferences can always depend on mood and time of day, what is your favorite
            type of beverage to receive as a welcome amenity where available?
          </p>
        </div>
        <Controller
          name={`${basePath}.beverage_preferences`}
          control={control}
          render={({ field }) => (
            <BadgeSelect<DrinkingPreference>
              label=""
              options={DRINKING_PREFERENCE_OPTIONS}
              value={field.value ?? []}
              onChange={(value) => {
                field.onChange(value);
                props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'beverage_preferences', value, `${basePath}.beverage_preferences`);
              }}
            />
          )}
        />
      </div>

      <div className="h-px bg-[#D9E2F3]" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">Pillow Preferences</h5>
        </div>
        <Controller
          name={`${basePath}.pillow_preferences`}
          control={control}
          render={({ field }) => (
            <BadgeSelect<PillowPreference>
              label=""
              options={PILLOW_PREFERENCE_OPTIONS}
              value={field.value ?? []}
              onChange={(value) => {
                field.onChange(value);
                props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'pillow_preferences', value, `${basePath}.pillow_preferences`);
              }}
            />
          )}
        />
      </div>

      <div className="h-px bg-[#D9E2F3]" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">
            Room Configuration Preferences
          </h5>
        </div>
        <Controller
          name={`${basePath}.room_configuration_preferences`}
          control={control}
          render={({ field }) => (
            <BadgeSelect<RoomPreference>
              label=""
              options={ROOM_PREFERENCE_OPTIONS}
              value={field.value ?? []}
              onChange={(value) => {
                field.onChange(value);
                props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'room_configuration_preferences', value, `${basePath}.room_configuration_preferences`);
              }}
            />
          )}
        />
      </div>

      <div className="h-px bg-[#D9E2F3]" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">Spa Preferences</h5>
        </div>
        <Controller
          name={`${basePath}.spa_preferences`}
          control={control}
          render={({ field }) => (
            <BadgeSelect
              label=""
              options={SPA_PREFERENCE_OPTIONS}
              value={field.value ?? []}
              onChange={(value) => {
                field.onChange(value);
                props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'spa_preferences', value, `${basePath}.spa_preferences`);
              }}
            />
          )}
        />
        <TextArea
          {...register(`${basePath}.spa_preferences_special_requests`, {
            onBlur: (e) => {
              props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'spa_preferences_special_requests', e.target.value, `${basePath}.spa_preferences_special_requests`);
            },
          })}
          disableResize
          placeholder="If you're an spa enthusiast, anything we should know like your preferred spa appointment time allows us to best make bookings on your behalf as requested."
          rows={4}
          errors={errors}
        />
      </div>

      <div className="h-px bg-[#D9E2F3]" />

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">General Likes & Dislikes</h5>
        </div>
        <TextArea
          {...register(`${basePath}.likes`, {
            onBlur: (e) => {
              props.onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.general, 'likes', e.target.value, `${basePath}.likes`);
            },
          })}
          disableResize
          label=""
          rows={4}
          errors={errors}
          placeholder="As we craft your trip, any and all unique likes and dislikes are helpful. Please use this box to share what a perfect stay looks like to you! Any turndown preferences? Things you like to have in your room on arrival like a steamer or extra hangers? The more specific, the better."
        />
      </div>
    </div>
  );
};

type TravelPreferencesTab = 'travel' | 'stay' | 'lifestyle' | 'other';

const getTravelPreferencesTabs = (props: TravelPreferencesByCategoryProps) =>
  ({
    travel: (
      <Section
        isLast
        className="px-0 py-0"
        description="These preferences are saved for the primary passenger. This information helps us plan a more comfortable travel experience."
      >
        <div className="mt-5">
          <TravelFlightPreferences {...props} />
        </div>
      </Section>
    ),
    stay: (
      <Section isLast className="px-0 py-0">
        <StayAccommodationPreferences {...props} />
      </Section>
    ),
    lifestyle: <TravelPreferencesLifestyle {...props} />,
    other: (
      <Section className="px-0 py-0" title="" isLast>
        <div className="rounded-[14px] bg-[#F2F6FC] flex flex-col gap-4">
          <ComfortDetails
            intent="primaryWhite"
            tabsIntent="default"
            helperText="Used only when helpful for specific adventure activities (e.g., required for African safaris, certain small airlines/aircraft, biking excursions, scuba diving, etc.)"
            className="bg-transparent"
            fieldPrefix="primary_passenger.preference_data"
            onUpdateTravelPreferencesWithRelatedData={props.onUpdateTravelPreferencesWithRelatedData}
          />
          <PersonalInfoSection
            basePath="primary_passenger.preference_data"
            onUpdateTravelPreferencesWithRelatedData={props.onUpdateTravelPreferencesWithRelatedData}
          />
        </div>
      </Section>
    ),
  }) as Record<TravelPreferencesTab, React.ReactNode>;

export const TravelPreferencesByCategory = (props: TravelPreferencesByCategoryProps) => {
  const [activeTab, setTab] = useState<TravelPreferencesTab>('travel');
  const tabs = [
    { id: 'travel', label: 'Travel' },
    { id: 'stay', label: 'Stay' },
    { id: 'lifestyle', label: 'Lifestyle' },
    { id: 'other', label: 'Personal' },
  ];
  const TRAVEL_PREFERENCES_TABS = getTravelPreferencesTabs(props);

  return (
    <div>
      <div className="my-5">
        <Tabs<TravelPreferencesTab>
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => {
            setTab(id);
          }}
          intent="third"
        />
      </div>
      <div>{TRAVEL_PREFERENCES_TABS[activeTab]}</div>
    </div>
  );
};